import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi } from "../services/api";
import Table from "../components/common/Table";

const AVATAR_COLORS = ["#3a47ff", "#10b981", "#f59e0b", "#ec4899", "#8b5cf6", "#06b6d4", "#ef4444", "#0a1530"];
const avatarColor = (s) => AVATAR_COLORS[s.charCodeAt(0) % AVATAR_COLORS.length];
const initials = (n) => n.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

const UsersPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.limit, query, filter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getUsers({
        page: pagination.page,
        limit: pagination.limit,
        search: query,
        status: filter !== "all" ? filter : undefined,
      });
      setUsers(response.data.users);
      setPagination((prev) => ({
        ...prev,
        ...response.data.pagination,
      }));
    } catch (err) {
      setError("Failed to load users");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const stats = useMemo(() => {
    const connected = users.filter(u => u.connections?.some(c => !c.isExpired)).length;
    const disconnected = users.filter(u => !u.connections || u.connections.length === 0).length;
    
    return {
      total: users.length,
      connected,
      disconnected,
    };
  }, [users]);

  const getUserStatus = (user) => {
    if (!user.connections || user.connections.length === 0) return "disconnected";
    const hasActive = user.connections.some(c => !c.isExpired);
    return hasActive ? "connected" : "disconnected";
  };

  const getLastSync = (user) => {
    // if (!user.connections || user.connections.length === 0) return "—";
    return new Date(user.createdAt || user.updatedAt).toLocaleString();
  };

  const handleUserClick = (userId) => {
    navigate(`/audit-logs?userId=${userId}`);
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination((prev) => ({ ...prev, limit: newPageSize, page: 1 }));
  };

  const columns = [
    {
      key: "user",
      label: "User",
      render: (u) => (
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-full grid place-items-center text-white font-semibold text-xs shrink-0" style={{ background: avatarColor(u.name) }}>
            {initials(u.name)}
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-ink text-[13px] truncate">{u.name}</div>
            <div className="text-muted text-xs truncate">{u.email}</div>
          </div>
        </div>
      ),
    },
    {
      key: "company",
      label: "Company",
      render: (u) => (
        u.connections && u.connections.length > 0 ? (
          <div className="text-xs text-ink">
            {u.connections.map((c, i) => (
              <div key={i} className="truncate">
                {c.companyName || c.realmId}
              </div>
            ))}
          </div>
        ) : (
          <span className="text-muted text-xs"> - </span>
        )
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (u) => (
        <span className={`inline-flex items-center gap-1 py-0.5 px-2 rounded-full text-[11px] font-semibold ${
          getUserStatus(u) === "connected" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
        }`}>
          <span className={`w-1.5 h-1.5 rounded-full ${
            getUserStatus(u) === "connected" ? "bg-green-600" : "bg-red-600"
          }`}></span>
          {getUserStatus(u)}
        </span>
      ),
    },
    {
      key: "source",
      label: "Source",
      render: (u) => <span className="text-ink text-xs">{u.source || "Unknown"}</span>,
    },
    {
      key: "lastUpdated",
      label: "Last Updated",
      render: (u) => <span className="text-muted text-xs">{getLastSync(u)}</span>,
    },
  ];

  const tableData = users.map(u => ({
    ...u,
    onClick: () => handleUserClick(u._id),
  }));

  return (
    <div className="px-6 py-8 pb-24 font-sans text-ink bg-white h-full">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight m-0">Users</h1>
          <p className="text-muted text-[15px] mt-2 mb-0">Manage accounts connected to your QuickBooks OAuth Server.</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="border border-line rounded-xl p-5 bg-white">
          <div className="text-xs text-muted uppercase tracking-wider font-semibold">Total users</div>
          <div className="text-[26px] font-bold mt-1.5 tracking-tight">{stats.total}</div>
        </div>
        <div className="border border-line rounded-xl p-5 bg-white">
          <div className="text-xs text-muted uppercase tracking-wider font-semibold">Connected</div>
          <div className="text-[26px] font-bold mt-1.5 tracking-tight text-green-700">{stats.connected}</div>
        </div>
        <div className="border border-line rounded-xl p-5 bg-white">
          <div className="text-xs text-muted uppercase tracking-wider font-semibold">Disconnected</div>
          <div className="text-[26px] font-bold mt-1.5 tracking-tight text-red-700">{stats.disconnected}</div>
        </div>
      </div>

      <div className="flex gap-3 items-center flex-wrap mb-5">
        <div className="flex-1 min-w-[240px] relative">
          <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <circle cx="11" cy="11" r="7"/>
            <path d="m20 20-3.5-3.5"/>
          </svg>
          <input
            type="text"
            placeholder="Search by name or email…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full py-2.5 px-3.5 pl-10 border border-line rounded-lg text-sm bg-soft transition-all focus:outline-none focus:border-indigo focus:bg-white focus:ring-3 focus:ring-indigo/10"
          />
        </div>
        <div className="flex border border-line rounded-lg overflow-hidden bg-white">
          {["all", "connected", "disconnected"].map((f) => (
            <button
              key={f}
              className={`border-0 py-2.5 px-3.5 text-[13px] font-medium cursor-pointer border-r border-line last:border-r-0 transition-all ${
                filter === f ? "bg-[#1e293b] text-white" : "text-muted hover:bg-soft"
              }`}
              onClick={() => setFilter(f)}
            >
              {f[0].toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <Table
        columns={columns}
        data={tableData}
        loading={loading}
        error={error}
        pagination={pagination}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        maxHeight="calc(100vh - 400px)"
        emptyState={
          <tr>
            <td colSpan={5} className="py-20 px-4">
              <div className="flex flex-col items-center justify-center text-center">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mb-4">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
                <h3 className="text-lg font-semibold text-ink mb-2">No Users Found</h3>
                <p className="text-sm text-muted max-w-md">
                  {query || filter !== "all"
                    ? "No users match your current filters. Try adjusting your search criteria."
                    : "No users have been registered yet. Users will appear here once they connect to QuickBooks."}
                </p>
              </div>
            </td>
          </tr>
        }
      />
    </div>
  );
};

export default UsersPage;
